import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
  retainSearchParams,
} from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { lazy } from 'react';
import { z } from 'zod';

import config from 'config';
import { useConsentedTrackers } from 'hooks/useConsentedTrackers';
import Signup from 'pages/Signup';
import { WelcomeViewTransitionStyles } from 'pages/Signup/components/BusinessTypeCard/viewTransitions.style';
import type { AppRouterContext } from 'providers/AppRouter';
import { oneTrustScript } from 'services/scripts/oneTrust';
import { isMonitoringAgentOrBot } from 'utils/browser';
import { useRouterSyncLanguage } from 'router/hooks/useRouterSyncLanguage';

const TanStackDevtools = config.DEV
  ? lazy(() =>
      Promise.all([
        import('@tanstack/react-devtools'),
        import('@tanstack/react-router-devtools'),
        import('@tanstack/react-query-devtools'),
      ]).then(([devtools, router, query]) => ({
        default: () => (
          <devtools.TanStackDevtools
            plugins={[
              {
                name: 'TanStack Query',
                render: <query.ReactQueryDevtoolsPanel />,
                defaultOpen: false,
              },
              {
                name: 'TanStack Router',
                render: <router.TanStackRouterDevtoolsPanel />,
                defaultOpen: false,
              },
            ]}
          />
        ),
      })),
    )
  : () => null;

// TanStack Router's search parser is JSON-first: a numeric-looking value like
// `?ajs_aid=12345` is parsed as the number 12345, not the string "12345".
// `z.coerce.string()` normalizes that back to "12345" instead of dropping it
// — ajs_aid must be forwarded to Amplitude whenever present (see
// docs/adr/010-product-analytics.md), not silently discarded because of how
// it happened to be typed. `fallback` is a safety net for anything coercion
// itself can't handle, matching the pattern already used for tracking params
// in github.com/gympass/partner-referral-web-app.
const rootSearchSchema = z.object({
  ajs_aid: fallback(z.coerce.string().optional(), undefined),
});

export const Route = createRootRouteWithContext<AppRouterContext>()({
  validateSearch: zodValidator(rootSearchSchema),
  search: {
    middlewares: [retainSearchParams(['ajs_aid'])],
  },
  component: function RootComponent() {
    useRouterSyncLanguage();
    useConsentedTrackers();

    return (
      <>
        <HeadContent />

        <WelcomeViewTransitionStyles />

        <Signup />

        <Scripts />

        <TanStackDevtools />
      </>
    );
  },
  scripts: () => {
    if (isMonitoringAgentOrBot()) {
      return [];
    }

    // Only the strictly-necessary OneTrust stub (C0001) loads declaratively,
    // first. Trackers are injected by useConsentedTrackers once their consent
    // category is granted — see that hook.
    return oneTrustScript ? [oneTrustScript] : [];
  },
});
